import React, { useState } from "react";
import axios from "axios";
import { useEffect } from "react";
import { getAPIEndpoint } from "views/config";
import {
  Text,
  Box,
  Flex,
  Grid,
  SimpleGrid,
  useColorModeValue,
  Spinner
} from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import Card from "components/card/Card";
import Table from 'components/table/Table';
import ProgressStats from "views/admin/default/components/ProgressStats";
import { recentTrxnsFields } from '../../models/data.js';
import { useHistory } from "react-router-dom";
import { logger } from "views/models/logging.js";
import { formatYMD } from "views/helpers/date.js";
import { abs } from "stylis";

export default function Dashboard() {
  const tableTxtColor = useColorModeValue("secondaryGray.900", "white");
  const [recentTrxns, setRecentTrxns] = useState([]);
  const [stats, setStats] = useState([]);
  const [soldCount, setSoldCount] = useState(0);
  const [transCount, setTransCount] = useState(0);
  const [balance, setBalance] = useState(0.00);
  const [isTableLoading, setIsTableloading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [transCountLoading, setTransCountLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  let navigate = useHistory();

  useEffect(() => {
    const clientInfo = sessionStorage.getItem('clientInfo');
    const key = sessionStorage.getItem('token');
    if (clientInfo) {
      const { agentId, role } = JSON.parse(clientInfo);
      if (role === 'Admin') {
        navigate.push('/clientmgt');
      }

      const apiHeader = {
        headers: {
          "authorization": `Bearer ${key}`
        }
      }

      const reportDate = new Date().toLocaleDateString("en-GB", { timeZone: 'Africa/Lagos' });

      const fetchData = () => {
        getStats(agentId, reportDate, apiHeader);
        getRecentTrxns(agentId, reportDate, apiHeader);
        getTrxnCount(agentId, reportDate, apiHeader);
        getBalance(agentId, reportDate, apiHeader);
      }
      fetchData();

      const intervalData = setInterval(fetchData, 300000);

      return () => {
        clearInterval(intervalData);
      }
    }
    else
      navigate.push('/auth');

  }, []);

  const getStats = async (agentId, date, header) => {
    setStatsLoading(true);
    const params = {
      agentId: agentId,
      transDate: formatYMD(date)
    };

    try {
      const response = await axios.post(getAPIEndpoint('stats'), params, header);
      if (response)
        processStats(response.data);
    } catch (error) {
      await logger({ task: "Get transaction stats", error: error.toString() });
    }
    setStatsLoading(false);
  }

  const getRecentTrxns = async (agentId, date, header) => {
    const params = {
      agentId: agentId,
      transDate: formatYMD(date),
      n: 10
    };

    try {
      setIsTableloading(true);
      const response = await axios.post(getAPIEndpoint('recent_trxns'), params, header);
      if (response) {
        const { status, data } = response.data;
        if (status === 'success' && data)
          setRecentTrxns(data);
        else
          setRecentTrxns([]);
      }
    } catch (error) {
      await logger({ task: "Get recent transactions", error: error.toString() });
    }
    setIsTableloading(false);
  }

  const getTrxnCount = async (agentId, date, header) => {
    setTransCountLoading(true);
    const params = {
      agentId: agentId,
      transDate: formatYMD(date),
      n: 10
    };
    try {
      const response = await axios.post(getAPIEndpoint('trxn_count'), params, header);
      if (response) {
        const { status, data } = response.data;
        if (status === 'success' && data)
          setTransCount(data);
        else
          setTransCount(0);
      }

    } catch (error) {
      await logger({ task: "Get transaction count", error: error.toString() });
    }
    setTransCountLoading(false);
  }

  const getBalance = async (agentId, date, header) => {
    setBalanceLoading(true);
    const params = {
      agentId: agentId,
      balanceDate: formatYMD(date)
    };
    try {
      const response = await axios.post(getAPIEndpoint('balance'), params, header);
      if (response) {
        const { status, data } = response.data;
        if (status === 'success' && data)
          setBalance(parseFloat(data[0].balance));
        else
          setBalance(0);
      }

    } catch (error) {
      await logger({ task: "Get balance", error: error.toString() });
    }
    setBalanceLoading(false);
  }

  const processStats = (results) => {
    const { status, data } = results;
    if (!(status === 'success' && data)) {
      return;
    }
    let mtnCount = 0;
    let gloCount = 0;
    let nineMobileCount = 0;
    let airtelCount = 0;
    let totalCount = 1;

    const transVolume = (telco) => {
      const telcoData = data.filter(v => v.telco === telco);
      if (telcoData.length > 0) {
        return (telcoData.map(v => v.count).reduce((sum, v) => sum + v, 0));
      }
      else
        return 0;
    };

    if (status === 'success') {
      mtnCount = transVolume('MTN');
      gloCount = transVolume('Glo');
      nineMobileCount = transVolume('Airtel');
      airtelCount = transVolume('9Mobile');
      totalCount = mtnCount + gloCount + nineMobileCount + airtelCount;
      setSoldCount(totalCount);
    }
    else {
      setSoldCount(0);
    }

    const stats = [
      {
        telco: "MTN",
        stat: [mtnCount, Math.round((mtnCount / totalCount) * 100)],
        color: 'yellow'
      },
      {
        telco: "Glo",
        stat: [gloCount, Math.round((gloCount / totalCount) * 100)],
        color: 'green'
      },
      {
        telco: "Airtel",
        stat: [airtelCount, Math.round((airtelCount / totalCount) * 100)],
        color: 'red'
      },
      {
        telco: "9Mobile",
        stat: [nineMobileCount, Math.round((nineMobileCount / totalCount) * 100)],
        color: 'teal'
      }
    ];
    setStats(stats);
  }

  const tableProps = {
    ...recentTrxnsFields,
    data: recentTrxns,
    toolbarIsVisible: false,
    pagingIsAllowed: false,
    fontSize: 12,
    loading: isTableLoading
  }
  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 4, "2xl": 4 }}
        gap='20px'
        mb='20px'>
        <MiniStatistics
          name='Wallet Balance'
          value={balanceLoading ? <Spinner /> : <Box color={balance > 0 ? 'green.600' : 'red.600'}>{abs(balance).toLocaleString('en-US')} NGN</Box>}
        />
        <MiniStatistics
          name='Success Rate'
          value={transCountLoading || statsLoading ? <Spinner /> : ((soldCount / ((transCount === 0) ? 1 : transCount)) * 100).toFixed(1).toString() + '%'}
        />
        <MiniStatistics
          name='TRSN Count'
          value={transCountLoading ? <Spinner /> : transCount.toLocaleString('en-US')}
        />
        <MiniStatistics
          name='Airtime/Data Sold'
          value={statsLoading ? <Spinner /> : soldCount.toLocaleString('en-US')}
        />
      </SimpleGrid>

      <Grid templateColumns={{ base: "1fr", md: "2fr 1fr", lg: "2fr 1fr" }} gap='20px' mb='20px' >
        <Card
          direction='column'
          w='100%'
          px='0px'
          overflowX={{ sm: "scroll", lg: "hidden" }}>
          <Flex px='4' justify='space-between' mb='20px' align='center'>
            <Text
              color={tableTxtColor}
              fontSize='22px'
              fontWeight='700'
              lineHeight='100%'>
              Recent Transactions
            </Text>
          </Flex>
          <Flex w='100%' direction='column' px="4">
            <Table {...tableProps} />
          </Flex>
        </Card>
        <ProgressStats data={stats} showSpinner={statsLoading} />
      </Grid>
    </Box>
  );
}
